import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, of } from 'rxjs';
import { IProduct } from 'src/app/models/products/product';
import { ProductsService } from 'src/app/services/products.service';
import { AlertService } from 'src/app/services/alert.service';
import { DialogResult } from 'src/app/models/dialog-result';
import { GenerateProductDataResult, ProductListResult } from 'src/app/models/products/product-result';
import {
  FileExtension,
  FilenameIdentifier,
  ProductDataFilenameConvention,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';
import {
  ProductDataFilenameConventionService,
} from 'src/app/services/product-data-filename-conventions/product-data-filename-convention.service';
import { buildFormatConvention } from 'src/app/utils/format-convention';

export interface CreateProductDataDialogData {
  readonly productIds: string[];
  readonly organizationId: string;
  readonly batchId: string;
}

export interface GenerateProductDataRequestExtras {
  readonly target: {
    readonly formats: readonly FileExtension[];
  };
}

export interface FilenameRow {
  readonly productId: string;
  readonly format: FileExtension;
  readonly filename: string;
}

const DEFAULT_IDENTIFIER: FilenameIdentifier = 'Barcode';
const DEFAULT_EXTENSION: FileExtension = 'Json';
const EXTENSION_TO_SUFFIX: Record<FileExtension, string> = {
  Json: 'json',
  Csv: 'csv',
};

@Component({
  selector: 'app-create-product-data',
  templateUrl: './create-product-data.component.html',
  styleUrls: ['./create-product-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class CreateProductDataComponent {
  readonly DialogResult = DialogResult;
  products: IProduct[] = [];
  isLoading = true;
  isGenerating = false;
  allProductsValid = true;

  availableFormats: readonly FileExtension[] = [DEFAULT_EXTENSION];
  selectedFormats: readonly FileExtension[] = [DEFAULT_EXTENSION];
  filenameIdentifier: FilenameIdentifier = DEFAULT_IDENTIFIER;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CreateProductDataDialogData,
    public dialogRef: MatDialogRef<CreateProductDataComponent>,
    private productsService: ProductsService,
    private alertService: AlertService,
    private pdfcService: ProductDataFilenameConventionService,
  ) {}

  ngOnInit(): void {
    this.loadFilenameConvention();
    this.loadProducts();
  }

  get allFormatsSelected(): boolean {
    return this.availableFormats.length > 0
      && this.availableFormats.every((format: FileExtension) => this.selectedFormats.includes(format));
  }

  get isCreateDisabled(): boolean {
    return this.isGenerating
      || !this.allProductsValid
      || this.selectedFormats.length === 0
      || this.hasBlockedRows;
  }

  get hasBlockedRows(): boolean {
    return this.products.some((product: IProduct) => this.isProductBlocked(product));
  }

  private isProductBlocked(product: IProduct): boolean {
    const raw: string | null = this.rawIdentifierValueOrNull(product);
    return raw == null || raw.trim().length === 0;
  }

  private rawIdentifierValueOrNull(product: IProduct): string | null {
    if (this.filenameIdentifier === 'Reference') {
      return product.reference;
    }
    if (this.filenameIdentifier === 'Description') {
      return product.description;
    }
    return product.barcode;
  }

  get totalFilesText(): string {
    const total: number = this.products.length * this.selectedFormats.length;
    return `Generating ${total} files`;
  }

  get validationFormats(): readonly string[] {
    return this.selectedFormats.map((format: FileExtension) => format.toLowerCase());
  }

  get validationIdentifier(): 'barcode' | 'reference' | 'description' {
    if (this.filenameIdentifier === 'Reference') {
      return 'reference';
    }
    if (this.filenameIdentifier === 'Description') {
      return 'description';
    }
    return 'barcode';
  }

  isFormatSelected(format: FileExtension): boolean {
    return this.selectedFormats.includes(format);
  }

  toggleFormat(format: FileExtension): void {
    this.selectedFormats = this.isFormatSelected(format)
      ? this.selectedFormats.filter((selected: FileExtension) => selected !== format)
      : this.availableFormats.filter((available: FileExtension) =>
          available === format || this.selectedFormats.includes(available),
        );
  }

  toggleSelectAll(): void {
    this.selectedFormats = this.allFormatsSelected ? [] : [...this.availableFormats];
  }

  toggleSelectAllFromShared(checked: boolean): void {
    this.selectedFormats = checked ? [...this.availableFormats] : [];
  }

  formatConvention(format: FileExtension): string {
    return buildFormatConvention(this.filenameIdentifier, format);
  }

  onValidationComplete(allValid: boolean): void {
    this.allProductsValid = allValid;
  }

  buildFilenameRows(): FilenameRow[] {
    return this.products.flatMap((product: IProduct) =>
      this.selectedFormats.map((format: FileExtension) => ({
        productId: product.id ?? '',
        format,
        filename: `${this.filenameBaseFor(product)}.${EXTENSION_TO_SUFFIX[format]}`,
      })),
    );
  }

  generate(): void {
    this.isGenerating = true;
    const extras: GenerateProductDataRequestExtras = {
      target: { formats: [...this.selectedFormats] },
    };
    this.productsService
      .generateProductData(this.data.productIds, extras)
      .subscribe({
        next: (result: GenerateProductDataResult) => {
          this.isGenerating = false;
          this.alertService.successToast(this.buildSuccessMessage(result));
          this.dialogRef.close(DialogResult.Success);
        },
        error: (error: unknown) => {
          this.isGenerating = false;
          this.handleGenerateError(error);
        },
      });
  }

  private handleGenerateError(error: unknown): void {
    if (this.isExtensionNotEnabled(error)) {
      this.alertService.errorToast(
        'A selected file format is not enabled for this organisation. Configure it in Settings > Product Data File Convention.',
      );
    }
  }

  private isExtensionNotEnabled(error: unknown): boolean {
    return typeof error === 'object'
      && error !== null
      && (error as { status?: number }).status === 400;
  }

  private buildSuccessMessage(result: GenerateProductDataResult): string {
    const skippedCount: number = result.skippedProductIds.length;
    return skippedCount > 0
      ? `Uploaded ${result.uploadedCount} product data files. ${skippedCount} products skipped (no barcode).`
      : `Uploaded ${result.uploadedCount} product data files.`;
  }

  private loadFilenameConvention(): void {
    this.pdfcService
      .getLatest(this.data.organizationId)
      .pipe(catchError(() => of<ProductDataFilenameConvention | null>(null)))
      .subscribe({
        next: (convention: ProductDataFilenameConvention | null) => {
          this.applyConvention(convention);
        },
      });
  }

  private applyConvention(convention: ProductDataFilenameConvention | null): void {
    const identifier: FilenameIdentifier = convention?.filename.identifier ?? DEFAULT_IDENTIFIER;
    const extensions: readonly FileExtension[] = (convention?.filename.extensions?.length ?? 0) > 0
      ? [...convention!.filename.extensions]
      : [DEFAULT_EXTENSION];
    this.filenameIdentifier = identifier;
    this.availableFormats = extensions;
    this.selectedFormats = extensions;
  }

  private loadProducts(): void {
    this.productsService.listProducts(this.data.batchId).subscribe({
      next: (result: ProductListResult) => {
        this.products = result.products.filter((product: IProduct) =>
          product.id != null && this.data.productIds.includes(product.id),
        );
        this.updateLoadingState();
      },
    });
  }

  private updateLoadingState(): void {
    this.isLoading = this.products.length === 0;
  }

  private filenameBaseFor(product: IProduct): string {
    const raw: string = this.rawIdentifierValue(product);
    return raw.replace(/ /g, '_');
  }

  private rawIdentifierValue(product: IProduct): string {
    if (this.filenameIdentifier === 'Reference') {
      return product.reference ?? '';
    }
    if (this.filenameIdentifier === 'Description') {
      return product.description ?? '';
    }
    return product.barcode ?? '';
  }
}
